import './pagination.less'
import { PAGE, ACTIVE_TAB, IS_ALL, LIMIT_DEFAULT, PAGE_SIZE, PAGE_SIZES } from '../../constants'
import { updateUrlPageFilters } from '../../utils/urlUtil'
import useUrlContext from '../../hooks/useUrlContext'
import { CustomPaginationProps } from '../TableCardViewV2/tableCardViewV2'
import { DropdownProps, PaginationItemProps, Select } from 'semantic-ui-react'
import React, { useMemo } from 'react'
import { Pagination, PaginationProps, Typography } from '@lightbeamai/design-system'
import { FormattedMessage, useIntl } from 'react-intl'
import classNames from 'classnames'

interface IProps {
  pageName?: string
  currentPage: number
  totalPages: number
  activeTab?: string
  allowCustomise?: boolean
  onPageChange?: (params: PaginationItemProps) => void
  total?: number
  pageSize?: number
  pageSizes?: number[]
  size?: PaginationProps['size']
  className?: string
  customPaginationProps?: CustomPaginationProps
  legendValuesLoading?: boolean
  compact?: boolean
  totalItemsForPagination?: number
  showSinglePage?: boolean
  singlePageText?: string | React.ReactNode
  showCustomTotal?: boolean
}

const PaginationView = (props: IProps): React.ReactElement => {
  const {
    pageName,
    pageSize: irregularPageSize,
    pageSizes: irregularPageSizes = [],
    allowCustomise = false,
    currentPage,
    totalPages,
    pageSize = LIMIT_DEFAULT,
    activeTab,
    onPageChange,
    total,
    size = 'small',
    customPaginationProps = {},
    className,
    legendValuesLoading = false,
    totalItemsForPagination = null,
    showSinglePage = false,
    singlePageText = '',
    showCustomTotal = false
  } = props
  const context = useUrlContext({ pageName: pageName || '' })
  const pageSizeValue =
    irregularPageSize || context.pageFilters[activeTab || IS_ALL][PAGE_SIZE] || LIMIT_DEFAULT
  const totalCount = total || totalPages * pageSize

  const handleOnChange = (page) => {
    if (pageName) {
      updateUrlPageFilters({
        pageName,
        pageFilters: { [ACTIVE_TAB]: activeTab || IS_ALL, [PAGE]: page }
      })
    }
    // for backwards compatibility
    if (typeof onPageChange === 'function') {
      onPageChange({
        activePage: page
      })
    }
  }

  const intl = useIntl()
  const pageSizeOptions = (irregularPageSizes.length ? irregularPageSizes : PAGE_SIZES).map(
    (size) => ({
      key: size,
      value: size,
      text: intl.formatMessage(
        { id: 'pageSize' },
        {
          size
        }
      )
    })
  )

  const handleChangePageSize = (_, data: DropdownProps) => {
    const value = data.value
    pageName &&
      updateUrlPageFilters({
        pageName: pageName,
        pageFilters: {
          [ACTIVE_TAB]: activeTab || IS_ALL,
          pageSize: value as number,
          page: 1
        }
      })
  }

  const itemRender = (_, type, originalElement) => {
    if (type === 'prev' || type === 'next') {
      return originalElement
    }
    return null // Return null for page numbers to hide them
  }

  const hidePageNumbersProps = {
    showSizeChanger: false,
    showQuickJumper: false,
    showTotal: false,
    itemRender,
    // total: totalCount,
    showNextPrev: customPaginationProps.hasPreviousPage || customPaginationProps.hasNextPage
  }
  const itemsOnPage = totalCount < pageSizeValue ? totalCount : pageSizeValue

  const { hidePageNumbers, showPaginationByDefault, footerPaginationText } = customPaginationProps

  const totalItemsForManyPagination = useMemo(() => {
    if (totalItemsForPagination && !hidePageNumbers) {
      return totalItemsForPagination
    }

    if (currentPage > 1) {
      if (totalCount < pageSizeValue) {
        return totalCount + (currentPage - 1) * pageSizeValue
      }
      return currentPage * pageSizeValue + 1
    }

    if (totalCount < pageSizeValue) {
      return totalCount
    }
    return pageSizeValue + 1
  }, [hidePageNumbers, totalItemsForPagination, currentPage, totalCount, pageSizeValue])

  if (itemsOnPage === 0 && !legendValuesLoading) return <></>

  const start = LIMIT_DEFAULT * (currentPage - 1)
  const first = +currentPage !== 1 ? start + 1 : start + 1
  const last = first + (totalCount || 0) - 1

  return (
    <>
      {hidePageNumbers && (
        <div className="table-legend xs-mb-0">
          <Typography type="medium">
            {total === LIMIT_DEFAULT ? (
              <FormattedMessage
                id={'table.legend.string.many'}
                values={{ name: footerPaginationText, first, last }}
              />
            ) : (
              <FormattedMessage
                id={'table.legend.string'}
                values={{
                  name: footerPaginationText,
                  first,
                  last,
                  total: start + (total as number)
                }}
              />
            )}
          </Typography>
        </div>
      )}
      {showCustomTotal && (
        <div className=" xs-ml-32">
          <Typography type="medium">
            {' '}
            {intl.formatMessage({ id: 'totalPages' })}: {totalPages}
          </Typography>
        </div>
      )}
      <div
        className={classNames('lb-page-controls', className)}
        style={hidePageNumbers ? { marginLeft: 'auto' } : undefined}
      >
        <Pagination
          defaultCurrent={currentPage}
          current={currentPage}
          onChange={handleOnChange}
          total={showPaginationByDefault ? totalItemsForManyPagination : totalCount}
          pageSize={pageSizeValue}
          size={size}
          showLoader={showSinglePage && singlePageText ? false : legendValuesLoading}
          hideLastPage={!hidePageNumbers}
          showSinglePage={showSinglePage}
          singlePageText={singlePageText}
          {...(hidePageNumbers ? hidePageNumbersProps : {})}
          showTotal={!showCustomTotal && !hidePageNumbers && !showSinglePage}
        />
        {allowCustomise && (
          <Select
            value={parseInt(pageSizeValue)}
            options={pageSizeOptions}
            onChange={handleChangePageSize}
            icon="chevron down"
          />
        )}
      </div>
    </>
  )
}

export default PaginationView
